import Vue from "vue";
import App from "./App.vue";
import "./registerServiceWorker";
import router from "./router";
import store from "./store";
import vuetify from "./plugins/vuetify";
var convert = require("cyrillic-to-latin");
import "leaflet/dist/leaflet.css";
import VueI18n from "vue-i18n";

// Import global styles
import "@/assets/styles/global.css";

import TextField from "@/components/global/fields/text-field";
import Autocomplete from "@/components/global/fields/autocomplete";
import TextArea from "@/components/global/fields/textarea";
import ShiftPicker from "@/components/global/pickers/ShiftPicker.vue";
import Dropdown from "@/components/global/fields/dropdown";
import GlobalConfirmBox from "@/components/global/GlobalConfirmBox.vue";
import MonthPicker from "@/components/global/pickers/MonthPicker.vue";
import DatePicker from "@/components/global/pickers/DatePicker.vue";
import DatetimePicker from "@/components/global/pickers/DatetimePicker.vue";
import TimePicker from "@/components/global/pickers/TimePicker.vue";
import PrimaryButton from "@/components/global/buttons/GlobalButtonPrimary.vue";
import CircleButton from "@/components/global/buttons/GlobalButtonCircle.vue";
import PrintButton from "@/components/global/buttons/GlobalButtonPrint.vue";
import ExcelButton from "@/components/global/buttons/GlobalButtonExcel.vue";
import EditButton from "@/components/global/buttons/GlobalButtonEdit.vue";
import RemoveButton from "@/components/global/buttons/GlobalButtonRemove.vue";
import GlobalDialogCard from "@/components/global/GlobalDialogCard.vue";
import GlobalLoader from "@/components/global/GlobalLoader.vue";
import PageHeader from "@/components/global/PageHeader.vue";
import PageToolbar from "@/components/global/PageToolbar.vue";

Vue.use(VueI18n);

// Language configuration with metadata
const languageConfig = {
  sr: {
    text: "Српски - ћирилица",
    locale: "sr-RS",
    flag: "🇷🇸",
    script: "cyrillic",
    messages: require("./locales/sr.json")
  },
  rs: {
    text: "Ћирилица",
    locale: "sr-RS",
    flag: "🇷🇸",
    script: "cyrillic",
    messages: require("./locales/rs.json")
  },
  "sr-lat": {
    text: "Srpski - Latinica",
    locale: "sr-RS",
    flag: "🇷🇸",
    script: "latin",
    messages: require("./locales/sr-lat.json")
  },
  "rs-lat": {
    text: "Latinica",
    locale: "sr-RS",
    flag: "🇷🇸",
    script: "latin",
    messages: require("./locales/rs-lat.json")
  },
  hr: {
    text: "Hrvatski",
    locale: "hr-HR",
    flag: "🇭🇷",
    script: "latin",
    messages: require("./locales/hr.json")
  },
  bs: {
    text: "Bosanski",
    locale: "bs-BA",
    flag: "🇧🇦",
    script: "latin",
    currency: "BAM",
    messages: require("./locales/bs.json")
  },
  en: {
    text: "English",
    locale: "en-US",
    flag: "🇺🇸",
    script: "latin",
    messages: require("./locales/en.json")
  },
  mk: {
    text: "Македонски",
    locale: "mk-MK",
    flag: "🇲🇰",
    script: "cyrillic",
    messages: require("./locales/mk.json")
  },
  si: {
    text: "Slovenščina",
    locale: "sl-SI",
    flag: "🇸🇮",
    script: "latin",
    messages: require("./locales/si.json")
  },
  ru: {
    text: "Русский",
    locale: "ru-RU",
    flag: "🇷🇺",
    script: "cyrillic",
    messages: require("./locales/ru.json")
  },
  zh: {
    text: "中文",
    locale: "zh-CN",
    flag: "🇨🇳",
    script: "latin",
    messages: require("./locales/zh.json")
  },
  es: {
    text: "Español",
    locale: "es-ES",
    flag: "🇪🇸",
    script: "latin",
    messages: require("./locales/es.json")
  },
  fr: {
    text: "Français",
    locale: "fr-FR",
    flag: "🇫🇷",
    script: "latin",
    messages: require("./locales/fr.json")
  },
  de: {
    text: "Deutsch",
    locale: "de-DE",
    flag: "🇩🇪",
    script: "latin",
    messages: require("./locales/de.json")
  },
  uz: {
    text: "Oʻzbekcha",
    locale: "uz-UZ",
    flag: "🇺🇿",
    script: "latin",
    messages: require("./locales/uz.json")
  },
  tr: {
    text: "Türkçe",
    locale: "tr-TR",
    flag: "🇹🇷",
    script: "latin",
    messages: require("./locales/tr.json")
  },
  it: {
    text: "Italiano",
    locale: "it-IT",
    flag: "🇮🇹",
    script: "latin",
    messages: require("./locales/it.json")
  }
};

// Extract messages for VueI18n
const messages = Object.keys(languageConfig).reduce((acc, key) => {
  acc[key] = languageConfig[key].messages;
  return acc;
}, {});

const i18n = new VueI18n({
  locale: "sr",
  fallbackLocale: "sr",
  messages,
  fallbackWarn: true,
  missingWarn: true,
  silentTranslationWarn: true,
});

// Make language config globally available
Vue.prototype.$languageConfig = languageConfig;
Vue.config.productionTip = false;
Vue.mixin({
  components: {
    GlobalConfirmBox,
    GlobalLoader,
    DatetimePicker,
    TextField,
    TextArea,
    ShiftPicker,
    Dropdown,
    Autocomplete,
    MonthPicker,
    CircleButton,
    DatePicker,
    TimePicker,
    PrintButton,
    ExcelButton,
    RemoveButton,
    EditButton,
    PrimaryButton,
    GlobalDialogCard,
    PageHeader,
    PageToolbar
  },
  methods: {
    // Get current language configuration
    getCurrentLanguageConfig() {
      return this.$languageConfig[this.$i18n.locale] || this.$languageConfig.sr;
    },

    // Get all available languages for language picker
    getAvailableLanguages() {
      return Object.keys(this.$languageConfig).map(key => ({
        code: key,
        ...this.$languageConfig[key]
      }));
    },

    // Get language text by code
    getLanguageText(languageCode) {
      return this.$languageConfig[languageCode]?.text || languageCode;
    },

    // Format date using current language locale
    localeDate(
      date,
      options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }
    ) {
      if (!date) return "";
      const currentConfig = this.getCurrentLanguageConfig();
      let locale = currentConfig.locale;
      if (currentConfig.script === "latin" && (locale === "sr-RS")) {
        locale = 'sr-Latn-RS';
      }
      if (options && options.format === "iso") {
        const formatter = new Intl.DateTimeFormat(locale, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });
        const parts = formatter.formatToParts(new Date(date));
        const year = parts.find((p) => p.type === "year")?.value || "";
        const month = parts.find((p) => p.type === "month")?.value || "";
        const day = parts.find((p) => p.type === "day")?.value || "";
        return `${year}-${month}-${day}`;
      }
      return new Date(date).toLocaleDateString(locale, options);
    },
    toLatin(string) {
      if (!string) return "";
      if (["sr", "rs", "ru"].includes(this.$i18n.locale)) {
        return string;
      } else {
        return convert(string);
      }
    },
    translate: function (string) {
      let restoran = "";
      if (
        this.userProfile &&
        this.userProfile.type &&
        this.userProfile.type == "customer"
      ) {
        restoran = this.$store.state.company.restaurant;
      } else if (
        this.userProfile &&
        this.userProfile.type &&
        this.userProfile.type == "company"
      ) {
        restoran = this.$store.state.company.restaurant;
      } else if (
        this.userProfile &&
        this.userProfile.type &&
        this.userProfile.type == "recipient"
      ) {
        // restoran = this.$store.state.company.restaurant
        restoran =
          this.$store.state.company && this.$store.state.company.restaurant
            ? this.$store.state.company.restaurant
            : this.$store.state.restaurant;
      } else {
        restoran = this.$store.state.restaurant;
      }

      if (restoran && restoran.lang == "ekavica") {
        let stringsE = {};
        stringsE["Извјештај"] = "Извештај";
        stringsE["Цијена"] = "Цена";
        stringsE["Успјешно испоручено"] = "Успешно испоручено";
        stringsE["Сриједа"] = "Среда";
        stringsE["Понедјељак"] = "Понедељак";
        stringsE["Недјеља"] = "Недеља";
        stringsE["Смјена 1"] = "Смена 1";
        stringsE["1. Смјена"] = "1. Смена";
        stringsE["Смјена 1"] = "Смена 1";
        stringsE["Смјена 2"] = "Смена 2";
        stringsE["2. Смјена"] = "2. Смена";
        stringsE["Смјена 3"] = "Смена 3";
        stringsE["3. Смјена"] = "3. Смена";
        stringsE["Смјена 4"] = "Смена 4";
        stringsE["Свјеж краставац"] = "Свеж краставац";
        stringsE["Прва смјена"] = "Прва смена";
        stringsE["Друга смјена"] = "Друга смена";
        stringsE["Трећа смјена"] = "Трећа смена";
        stringsE[
          "НАРУЧИВАЊЕ, ОТКАЗИВАЊЕ, ПРОМЈЕНА СМЈЕНЕ ЗА НАРЕДНУ СЕДМИЦУ ОБАВЕЗНО ДО "
        ] =
          "НАРУЧИВАЊЕ, ОТКАЗИВАЊЕ, ПРОМЕНА СМЕНЕ ЗА НАРЕДНУ СЕДМИЦУ ОБАВЕЗНО ДО ";
        stringsE["НАРУЧИВАЊЕ, ОТКАЗИВАЊЕ, ПРОМЈЕНА СМЈЕНЕ ОБАВЕЗНО"] =
          "НАРУЧИВАЊЕ, ОТКАЗИВАЊЕ, ПРОМЕНА СМЕНЕ ОБАВЕЗНО";
        stringsE["НАРУЧИВАЊЕ, ОТКАЗИВАЊЕ, ПРОМЈЕНА СМЈЕНЕ"] =
          "НАРУЧИВАЊЕ, ОТКАЗИВАЊЕ, ПРОМЕНА СМЕНЕ";
        if (Object.keys(stringsE).includes(string)) {
          string = stringsE[string];
        }
      }

      if (typeof string !== "string") return string;

      const convertToCyrillic = (str) => {
        const latinToCyrMapping = {
          a: "а",
          b: "б",
          v: "в",
          g: "г",
          d: "д",
          đ: "ђ",
          dj: "ђ",
          e: "е",
          ž: "ж",
          z: "з",
          i: "и",
          j: "ј",
          k: "к",
          l: "л",
          lj: "љ",
          m: "м",
          n: "н",
          nj: "њ",
          o: "о",
          p: "п",
          r: "р",
          s: "с",
          t: "т",
          ć: "ћ",
          u: "у",
          f: "ф",
          h: "х",
          c: "ц",
          č: "ч",
          dž: "џ",
          š: "ш",
          A: "А",
          B: "Б",
          V: "В",
          G: "Г",
          D: "Д",
          Đ: "Ђ",
          DJ: "Ђ",
          E: "Е",
          Ž: "Ж",
          Z: "З",
          I: "И",
          J: "Ј",
          K: "К",
          L: "Л",
          Lj: "Љ",
          M: "М",
          N: "Н",
          Nj: "Њ",
          O: "О",
          P: "П",
          R: "Р",
          S: "С",
          T: "Т",
          Ć: "Ћ",
          U: "У",
          F: "Ф",
          H: "Х",
          C: "Ц",
          Č: "Ч",
          Dž: "Џ",
          Š: "Ш",
          LJ: "Љ",
          NJ: "Њ",
          DŽ: "Џ",
        };

        let cyrillicText = "";
        let i = 0;
        while (i < str.length) {
          // Check for digraphs (lj, nj, dž) first
          const digraph = str.substring(i, i + 2);
          const upperCaseDigraph = str.substring(i, i + 2).toUpperCase();

          if (latinToCyrMapping[digraph]) {
            cyrillicText += latinToCyrMapping[digraph];
            i += 2;
          } else if (latinToCyrMapping[upperCaseDigraph]) {
            cyrillicText += latinToCyrMapping[upperCaseDigraph];
            i += 2;
          } else {
            // Check for single-letter mappings
            const char = str[i];
            if (latinToCyrMapping[char]) {
              cyrillicText += latinToCyrMapping[char];
            } else {
              // If no mapping is found, keep the original character
              cyrillicText += char;
            }
            i++;
          }
        }
        return cyrillicText;
      };
      if (["sr", "rs"].includes(this.$i18n.locale)) {
        return convertToCyrillic(string);
      } else {
        return convert(string);
      }
    },
    customSearch(items, headers, search, additionalSearchFields = []) {
      if (!search || search.trim() === "") {
        return items;
      }
      if (!Array.isArray(items) || !Array.isArray(headers)) {
        return items; // Or handle error appropriately
      }

      // Normalize function to handle latin/cyrillic matching
      const normalize = (text) => {
        if (!text) return '';
        const str = String(text).toLowerCase();

        // Map for cyrillic to latin
        const cyrToLat = {
          'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'ђ': 'đ', 'е': 'e',
          'ж': 'ž', 'з': 'z', 'и': 'i', 'ј': 'j', 'к': 'k', 'л': 'l', 'љ': 'lj',
          'м': 'm', 'н': 'n', 'њ': 'nj', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's',
          'т': 't', 'ћ': 'ć', 'у': 'u', 'ф': 'f', 'х': 'h', 'ц': 'c', 'ч': 'č',
          'џ': 'dž', 'ш': 'š'
        };

        // Convert cyrillic to latin
        let normalized = '';
        for (let i = 0; i < str.length; i++) {
          const char = str[i];
          normalized += cyrToLat[char] || char;
        }

        // Remove diacritics from latin characters
        normalized = normalized
          .replace(/đ/g, 'd')
          .replace(/č/g, 'c')
          .replace(/ć/g, 'c')
          .replace(/š/g, 's')
          .replace(/ž/g, 'z');

        return normalized;
      };

      const searchTerm = normalize(search.trim());

      return items.filter((item) => {
        return headers.some((header) => {
          const propertyName = header.value;
          let propertyValue;

          if (propertyName.includes(".")) {
            propertyValue = propertyName.split(".").reduce((obj, key) => {
              return obj && typeof obj === "object" && obj[key] !== undefined
                ? obj[key]
                : undefined;
            }, item);
          } else {
            propertyValue = item[propertyName];
          }

          if (propertyValue !== null && typeof propertyValue !== "undefined") {
            const valueToSearch = normalize(propertyValue);
            return valueToSearch.includes(searchTerm);
          }
          return false;
        }) || additionalSearchFields.some((field) => {
          const fieldValue = item[field];
          if (fieldValue !== null && typeof fieldValue !== "undefined") {
            const valueToSearch = normalize(fieldValue);
            return valueToSearch.includes(searchTerm);
          }
          return false;
        });
      });
    },
  },
  computed: {
    userProfile() {
      return this.$store.state.user.user;
    },
  },
});

// Device mixin: isMobile, isTablet, isDesktop (reactive on resize)
Vue.mixin({
  data() {
    return {
      device: {
        isMobile: false,
        isTablet: false,
        isDesktop: false,
      },
    };
  },
  computed: {
    isMobile() {
      return this.device.isMobile;
    },
    isTablet() {
      return this.device.isTablet;
    },
    isDesktop() {
      return this.device.isDesktop;
    },
  },
  created() {
    this.$_updateDeviceType();
    window.addEventListener("resize", this.$_updateDeviceType);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.$_updateDeviceType);
  },
  methods: {
    $_updateDeviceType() {
      const width = window.innerWidth;
      this.device.isMobile = width <= 600;
      this.device.isTablet = width > 600 && width < 1024;
      this.device.isDesktop = width >= 1024;
    },
  },
});

new Vue({
  router,
  store,
  vuetify,
  i18n,
  render: (h) => h(App),
  watch: {
    // Sync Vuetify locale with i18n locale
    '$i18n.locale'(newLocale) {
      this.$vuetify.lang.current = newLocale;
    }
  },
  created() {
    // Set initial Vuetify locale
    this.$vuetify.lang.current = this.$i18n.locale;
  }
}).$mount("#app");
